import React, { useState, useEffect } from "react";
import Button from "../form/Button";
import Input from "../form/Input";
import { Editor } from "react-draft-wysiwyg";
import { EditorState, ContentState, convertToRaw } from "draft-js";
import htmlToDraft from "html-to-draftjs";
import draftToHtml from "draftjs-to-html";
import { postData } from "../../services/api";
import toastr from "toastr";
import { useTranslation } from "react-i18next";
import { classNames } from "../../helper/classNames";
import { useDebounce } from "../../helpers/useDebounce";

// 💡 Function to dynamically generate style map from inline `style="color:..."`
function generateColorStyleMapFromHTML(html) {
  const colorRegex = /color:\s*(#[0-9a-fA-F]{3,6}|rgb\([^)]+\))/gi;
  const styleMap = {};
  let match;

  while ((match = colorRegex.exec(html)) !== null) {
    const rawColor = match[1].trim();
    const colorKey = `COLOR_${rawColor.replace(/[^a-zA-Z0-9]/g, "_")}`;
    if (!styleMap[colorKey]) {
      styleMap[colorKey] = { color: rawColor };
    }
  }

  return styleMap;
}

const CompanyPositionCompetenciesPopup = ({
  isOpen,
  setIsOpen = () => {},
  data,
  setData = () => {},
  loadList = () => {},
  isAdmin = false,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState(() =>
    EditorState.createEmpty()
  );
  const [customStyleMap, setCustomStyleMap] = useState({});
  const [positions, setPositions] = useState([]);

  const closeModal = () => {
    setIsOpen(false);
    setData({});
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const rawContentState = convertToRaw(description.getCurrentContent());
    const html = draftToHtml(rawContentState);
    const payload = data?._id
      ? { name, position_summary: html, id: data?._id }
      : { name, position_summary: html };
    const url = data?._id
      ? "/position-competency-update"
      : "/position-competency-create";

    try {
      setLoading(true);
      const result = await postData(url, payload);
      if (result.status === 200) {
        toastr.success(result.message);
        closeModal();
        setLoading(false);
        loadList();
      } else {
        toastr.error(result.message);
        setLoading(false);
        closeModal();
      }
    } catch (error) {
      toastr.error(error.message);
      setLoading(false);
      closeModal();
    }
  };

  const onEditorStateChange = (editorState) => {
    setDescription(editorState);
  };

  const search = useDebounce(name, 500);

  useEffect(() => {
    // Position List
    const loadPosition = async () => {
      setLoading(true);
      try {
        const result = await postData("company-position/list", {
          keyword: search,
          limit: 1000000000,
          isGlobal: true,
        });
        if (result.data) {
          setPositions(result.data);
        }
        setLoading(false);
      } catch (err) {
        setLoading(false);
        console.log(err.message);
      }
    };
    loadPosition();
  }, [search]);

  useEffect(() => {
    if (data?._id) {
      setName(data?.name);
      const html = data?.position_competencies || "";
      const contentBlock = htmlToDraft(html);
      const contentState = ContentState.createFromBlockArray(
        contentBlock.contentBlocks,
        contentBlock.entityMap
      );
      const editorState = EditorState.createWithContent(contentState);
      setDescription(editorState);

      // Dynamically generate custom style map from HTML
      const styleMap = generateColorStyleMapFromHTML(html);
      setCustomStyleMap(styleMap);
    }
  }, [data]);

  return (
    <>
      <div className="relative z-50 overflow-hidden w-full">
        <div
          className={classNames(
            "fixed inset-0 bg-black bg-opacity-25 transition-all duration-300 ease-in-out",
            isOpen ? "opacity-100 visible" : "opacity-0 invisible"
          )}
        />
        <div
          className={classNames(
            "fixed inset-0 overflow-y-auto transition-transform duration-300",
            isOpen ? "opacity-100 visible" : "opacity-0 invisible"
          )}
        >
          <div className="flex h-screen justify-end w-full overflow-hidden">
            <div
              className={classNames(
                "w-full flex flex-col max-w-2xl rounded-l-2xl p-4 bg-white text-left align-middle shadow-xl transition-all duration-300",
                isOpen ? "translate-x-0" : "translate-x-full"
              )}
            >
              <div className="relative flex-shrink-0">
                <h3 className="text-lg font-medium leading-6 text-gray-900 text-center mb-4">
                  Manage Position Competencies
                </h3>
                <div className="absolute -top-4 -right-4 z-10">
                  <Button
                    buttonLabelClasses=""
                    buttonType="button"
                    buttonIcon="fa-light fa-times"
                    buttonIconPosition="left"
                    buttonLabel=""
                    buttonClasses="!bg-transparent !text-slate-700 !w-10 !h-10 !p-0"
                    functions={closeModal}
                    buttonHasLink={false}
                  />
                </div>
              </div>

              <div className="space-y-4 px-2 py-2 mt-3 overflow-auto scroll-smooth scrollbar max-h-[calc(100vh-150px)]">
                <div className="relative space-y-2">
                  {/* <div className="relative w-full">
                    <Input
                      requiredDisplay={true}
                      label={t("position_name")}
                      labelClasses="!text-xs"
                      inputType="text"
                      inputPlaceholder={t("enter_position_name")}
                      inputValue={name}
                      setInput={setName}
                    />
                  </div> */}
                  <div className="relative w-full group z-10">
                    <Input
                      label={t("position")}
                      requiredDisplay={true}
                      labelClasses={"!text-xs"}
                      inputType={"text"}
                      inputPlaceholder={t("enter_position")}
                      inputValue={name}
                      setInput={(val) => {
                        setName(val);
                        // createAcronym(val);
                      }}
                    />
                    {positions &&
                      Array?.isArray(positions) &&
                      positions?.length > 0 && (
                        <div className="hidden group-focus-within:block absolute top-full left-0 w-full mt-1">
                          <div className="relative bg-white border border-slate-100 shadow-md py-1 overflow-hidden rounded-lg">
                            <div className="overflow-auto scrollbar max-h-60">
                              {positions &&
                                Array?.isArray(positions) &&
                                positions?.length > 0 &&
                                positions?.map((item) => (
                                  <>
                                    <Button
                                      buttonLabel={
                                        item?.name + " - " + item?.short_name
                                      }
                                      buttonLabelClasses={"!text-xs"}
                                      buttonClasses={
                                        "w-full !h-8 !justify-start !bg-transparent hover:!bg-slate-100 !text-slate-500 hover:!text-600"
                                      }
                                      functions={(e) => {
                                        setName(item?.name);
                                        // createAcronym(item?.name);
                                        // setShortName(item?.short_name);
                                      }}
                                    />
                                  </>
                                ))}
                            </div>
                          </div>
                        </div>
                      )}
                  </div>
                  <div className="relative w-full">
                    <div className="editor-div">
                      <Editor
                        editorState={description}
                        toolbarClassName="toolbarClassName"
                        wrapperClassName="wrapperClassName"
                        editorClassName="editorClassName"
                        onEditorStateChange={onEditorStateChange}
                        customStyleMap={customStyleMap}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="relative flex justify-center mt-2 gap-4">
                <Button
                  buttonClasses="!bg-teal-500"
                  buttonLabelClasses=""
                  buttonType="button"
                  buttonIcon={
                    loading
                      ? "fa-light fa-spinner fa-spin"
                      : "fa-light fa-check"
                  }
                  buttonIconPosition="left"
                  buttonLabel={t("confirm")}
                  functions={onSubmit}
                  buttonHasLink={false}
                />
                <Button
                  buttonLabelClasses=""
                  buttonType="button"
                  buttonIcon="fa-light fa-times"
                  buttonIconPosition="left"
                  buttonLabel={t("cancel")}
                  buttonClasses="!bg-slate-200 !text-slate-600 border border-slate-300"
                  functions={closeModal}
                  buttonHasLink={false}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CompanyPositionCompetenciesPopup;
